'use client'

import {userKeys} from "@/hooks/queries/queryKeys";
import {useCallback} from "react";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {useQuery} from "@tanstack/react-query";
import {useShallow} from "zustand/react/shallow";
import {userSubscriptionAPI, userUsageAPI} from "@/app/apiPaths";
import {GetSubscriptionResponse, GetUsageSummaryResponse} from "@/pb/api/devplan/services/web/user";
import {useSession} from "next-auth/react";
import {CompanyDetails, UserCompanyInvite} from "@/pb/api/devplan/types/company";
import {SubscriptionDetails} from "@/pb/api/devplan/types/usage";
import {jr} from "@/hooks/queries/response-handler";

export function useUsers(companyId: number) {
  const {fetchCompanyUsers} = useBoundStore()
  const queryKey = userKeys.all(companyId)
  const queryFn = useCallback(async () => fetchCompanyUsers(companyId), [companyId, fetchCompanyUsers])
  const {data, isPending: loading, error} = useQuery({queryKey, queryFn})
  const users = useBoundStore(useShallow(s => s.getCompanyUsers(companyId)))
  return {users: data == undefined ? undefined : users, loading, error}
}

export function useOrgUsers(orgId: string) {
  const {fetchOrgUsers} = useBoundStore()
  const queryKey = userKeys.org(orgId)
  const queryFn = useCallback(async () => fetchOrgUsers(orgId), [orgId, fetchOrgUsers])
  const {isPending: loading, error} = useQuery({queryKey, queryFn})
  return {loading, error}
}

export function useCachedCompanyUserIds(companyId: number): string[]  {
  return useBoundStore(useShallow(s =>
    Object.keys(s.userCompanyDetails[companyId] || {}).toSorted((a, b) => a.localeCompare(b))))
}

export function useUser() {
  const {data: session, update} = useSession()
  const userId = session?.user?.id
  const {fetchSelf} = useBoundStore()
  const queryKey = userKeys.current
  const queryFn = useCallback(async () => {
    return await fetchSelf().finally(() => {
      update()
      // Just in case, reload a session again
      setTimeout(() => update(), 3000)
    })
  }, [fetchSelf, update])
  const {data: dbUserId, isPending: loading, error, refetch: reload} = useQuery({queryKey, queryFn})
  const id = userId || dbUserId
  const user = useBoundStore(useShallow(s => id ? s.getUser(id) : undefined))
  return {user, loading, error, reload}
}

export function useUserLimits() {
  const {fetchOwnLimits} = useBoundStore()
  const queryKey = userKeys.limits()
  const queryFn = useCallback(async () => {
    await fetchOwnLimits()
    return true
  }, [fetchOwnLimits])
  const {isPending: loading, error, refetch: reload} = useQuery({queryKey, queryFn})
  const limits = useBoundStore(useShallow(s => s.ownLimits))
  return {limits, loading, error, reload}
}

export function useOwnCompanies(): CompanyDetails[] {
  return useBoundStore(useShallow(s => s.ownCompanyUserDetails.map(d => s.companies[d.companyId])
    .filter(c => c !== undefined)))
}

export function useOwnInvites(): UserCompanyInvite[] {
  return useBoundStore(useShallow(s => s.ownInvites))
}

export function useUserUsageSummary() {
  const queryKey = userKeys.usage()
  const queryFn = useCallback(async () => fetch(userUsageAPI())
      .then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
      .then(js => GetUsageSummaryResponse.fromJSON(js).summary!)
    , [])
  const {data: summary, isPending: loading, error} = useQuery({queryKey, queryFn})
  return {summary, loading, error}
}

export function useUserPreferences() {
  const {fetchUserPreferences} = useBoundStore()
  const queryKey = userKeys.preferences()
  const queryFn = useCallback(async () => await fetchUserPreferences() ?? true, [fetchUserPreferences])
  const {isPending: loading, error} = useQuery({queryKey, queryFn})
  const preferences = useBoundStore(useShallow(s => s.userPreferences))
  return {preferences, loading, error}
}

export function useUserPlans() {
  const {fetchPlans} = useBoundStore()
  const queryKey = userKeys.plans()
  const queryFn = useCallback(async () => await fetchPlans() ?? true, [fetchPlans])
  const {isPending: loading, error} = useQuery({queryKey, queryFn})
  const plans = useBoundStore(useShallow(s => s.plans))
  return {plans, loading, error}
}

export function usePersonalSubscription() {
  const queryKey = userKeys.subscription()
  const queryFn = useCallback(async (): Promise<SubscriptionDetails | null> => {
    return fetch(userSubscriptionAPI())
      .then(jr)
      .then(js => GetSubscriptionResponse.fromJSON(js).subscription ?? null)
  }, [])
  const {data: subscription, isPending: loading, error} = useQuery({queryKey, queryFn})
  return {subscription, loading, error}
}
